import React from 'react';
import type { FC } from 'react';
import { css } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import SectionMessage from '@atlaskit/section-message/section-message';
import { token } from '@atlaskit/tokens';

import type { MeetingsEligibility } from './meetingTypes.ts';

const i18n = defineMessages({
	noTwcTitle: {
		id: 'feed.meetings.eligibility.no-twc.title',
		defaultMessage: 'Catch up on your meetings in Confluence',
		description: 'Title of the banner shown to users without Teamwork Collaboration',
	},
	noTwcBody: {
		id: 'feed.meetings.eligibility.no-twc.body',
		defaultMessage:
			'Set up Loom AI meeting notes to see your recorded meetings and action items right here.',
		description: 'Body of the banner shown to users without Teamwork Collaboration',
	},
	noTwcCta: {
		id: 'feed.meetings.eligibility.no-twc.cta',
		defaultMessage: 'Get started',
		description: 'CTA to cross-flow into Teamwork Collaboration setup',
	},
	noCalendarTitle: {
		id: 'feed.meetings.eligibility.no-calendar.title',
		defaultMessage: 'Connect your calendar',
		description: 'Title of the banner shown to users who have not connected a calendar',
	},
	noCalendarBody: {
		id: 'feed.meetings.eligibility.no-calendar.body',
		defaultMessage:
			'Connect your calendar so Loom can record your meetings and surface action items here.',
		description: 'Body of the banner shown to users who have not connected a calendar',
	},
	noCalendarCta: {
		id: 'feed.meetings.eligibility.no-calendar.cta',
		defaultMessage: 'Connect calendar',
		description: 'CTA to connect the user calendar to Loom',
	},
});

const wrapperStyle = css({
	marginTop: token('space.100'),
	marginBottom: token('space.200'),
});

type MeetingsEligibilityBannerProps = {
	eligibility: Extract<MeetingsEligibility, 'noTwc' | 'noConnectedCalendar'>;
	onCtaClick?: () => void;
};

/**
 * Banner shown when the user is not (yet) eligible to see meetings:
 * - noTwc: cross-flow to provision Teamwork Collaboration + notetaker
 * - noConnectedCalendar: prompt to connect a calendar
 */
export const MeetingsEligibilityBanner: FC<MeetingsEligibilityBannerProps> = ({
	eligibility,
	onCtaClick,
}) => {
	const intl = useIntl();
	const isNoTwc = eligibility === 'noTwc';
	const title = isNoTwc ? i18n.noTwcTitle : i18n.noCalendarTitle;
	const body = isNoTwc ? i18n.noTwcBody : i18n.noCalendarBody;
	const cta = isNoTwc ? i18n.noTwcCta : i18n.noCalendarCta;

	return (
		<div css={wrapperStyle} data-testid={`meetings-eligibility-banner-${eligibility}`}>
			<SectionMessage title={intl.formatMessage(title)}>
				<Text as="p">
					<FormattedMessage {...body} />
				</Text>
				<Box paddingBlockStart="space.100">
					<Button appearance="primary" onClick={onCtaClick} testId="meetings-eligibility-cta">
						<FormattedMessage {...cta} />
					</Button>
				</Box>
			</SectionMessage>
		</div>
	);
};
